import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { GenericFormHandlerService } from '../shared/generic-crud.service';
import { ProductosService } from '../services/productos.service';
import { VentasService } from '../services/ventas.service';
import { PagosService } from '../services/pagos.service';
import { ToastrService } from 'ngx-toastr';
import { debounceTime, distinctUntilChanged, switchMap, catchError, of, Subscription } from 'rxjs';
import { AuthService, AuthUser } from '../services/auth.service';
import { Capacitor } from '@capacitor/core';
import { Filesystem, Directory, Encoding } from '@capacitor/filesystem';
import { Share } from '@capacitor/share';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { MetodoPago, METODO_PAGO_LABELS } from '../enums/metodo-pago.enum';

@Component({
  selector: 'app-inicio',
  templateUrl: './inicio.component.html',
  styleUrls: ['./inicio.component.scss']
})
export class InicioComponent extends GenericFormHandlerService implements OnInit, OnDestroy {
  // Definición con inicialización para evitar errores
  busquedaForm: FormGroup = this.fb.group({
    busqueda: ['', [Validators.required, Validators.minLength(2)]]
  });

  productoForm: FormGroup = this.fb.group({
    id: [null],
    codigo: [''],
    nombre: [''],
    modoUso: [''],
    mocontenidodoUso: [''],
    tipo: [''],
    precio: [0],
    stock: [0],
    cantidad: [1, [Validators.required, Validators.min(1)]]
  });

  ventaForm: FormGroup = this.fb.group({
    dineroRecibido: [null, [Validators.required, Validators.min(0)]],
    total: [0],
    cambio: [null],
    usuarioId: [null],
    esApartado: [false],
    metodoPago: [MetodoPago.EFECTIVO]
  });

  MetodoPago = MetodoPago;
  metodosPago = Object.values(MetodoPago);
  metodoPagoLabels = METODO_PAGO_LABELS;

  // Búsqueda
  productosEncontrados: Producto[] = [];
  mostrarSugerencias: boolean = false;
  buscando: boolean = false;

  // Carrito
  productosEnVenta: ProductoEnVenta[] = [];
  displayedColumns: string[] = ['codigo', 'nombre', 'precio', 'cantidad', 'subtotal', 'acciones'];

  // Checkboxes para generación de tickets (Web y Móvil)
  generarPdf: boolean = false;
  generarTicketImpresora: boolean = false;

  private currentUserSubscription?: Subscription;

  constructor(
    protected fb: FormBuilder,
    protected productosService: ProductosService,
    protected ventasService: VentasService,
    protected toastr: ToastrService,
    protected cdRef: ChangeDetectorRef,
    protected authService: AuthService,
    public pagosService: PagosService
  ) {
    super(ventasService, toastr);
    this.inicializarFormularios();
    this.configurarBusquedaReactiva();
  }

  ngOnInit(): void {
    // Suscribirse al observable de usuario
    // El app.component.ts ya se encarga de asegurar que el usuario esté cargado
    // Esta suscripción recibirá el usuario cuando esté disponible
    this.currentUserSubscription = this.authService.currentUser$.subscribe(user => {
      if (user) {
        this.setUsuarioId(user);
      }
    });

    // Obtener usuario actual si ya está cargado (puede estar disponible inmediatamente)
    // Esto es importante para casos donde el usuario ya se cargó antes de que
    // este componente se inicializara
    const currentUser = this.authService.getCurrentUserSnapshot();
    if (currentUser) {
      this.setUsuarioId(currentUser);
    }
  }

  override ngOnDestroy(): void {
    this.currentUserSubscription?.unsubscribe();
    super.ngOnDestroy();
  }

  private inicializarFormularios(): void {

  }

  private configurarBusquedaReactiva(): void {
    this.busquedaForm.get('busqueda')?.valueChanges.pipe(
      debounceTime(300),
      distinctUntilChanged(),
      switchMap(termino => {
        this.mostrarSugerencias = termino.length >= 2;
        this.buscando = this.mostrarSugerencias;

        return termino.length >= 2
          ? this.productosService.buscarProductos(termino).pipe(
              catchError(() => {
                this.toastr.error('Error al buscar productos');
                return of([]);
              })
            )
          : of([]);
      })
    ).subscribe({
      next: (productos) => {
        setTimeout(() => {
          this.productosEncontrados = productos;
          this.buscando = false;
        });
      }
    });
  }


// Agrega este nuevo método en tu componente
agregarProductoDirecto(producto: any): void {
  // Verificar stock - convertir a número si viene como string
  const stock = Number(producto.stock) || 0;
  if (stock <= 0) {
    this.toastr.warning('Este producto no tiene stock disponible');
    return;
  }

  // Buscar si el producto ya está en el carrito
  const index = this.productosEnVenta.findIndex(p => p.id === producto.id);

  if (index >= 0) {
    // Actualizar cantidad si ya existe
    const cantidadActual = Number(this.productosEnVenta[index].cantidad) || 0;
    const nuevaCantidad = cantidadActual + 1;
    const stockNumerico = Number(producto.stock) || 0;

    if (nuevaCantidad > stockNumerico) {
      this.toastr.warning(`No hay suficiente stock para agregar más unidades. Disponible: ${stockNumerico}`);
      return;
    }

    this.productosEnVenta[index].cantidad = nuevaCantidad;
    this.actualizarSubtotalProducto(this.productosEnVenta[index]);
  } else {
    // Convertir precio a número si viene como string
    const precioNumerico = typeof producto.precio === 'string' 
      ? parseFloat(producto.precio) 
      : Number(producto.precio) || 0;
    
    const primerProducto: ProductoEnVenta = {
      id: producto.id,
      codigo: producto.codigo,
      nombre: producto.nombre,
      tipo: producto.tipo,
      precio: precioNumerico,
      stock: Number(producto.stock) || 0,
      peso: typeof producto.peso === 'string' ? parseFloat(producto.peso) : Number(producto.peso) || 0,
      contenido: producto.contenido,
      unidadMedida: producto.unidadMedida,
      modoUso: producto.modoUso,
      cantidad: 1,
      descuento: 0,
      subtotal: precioNumerico // Asegurar que subtotal sea número desde el inicio
    };
    this.productosEnVenta.push(primerProducto);
    //Agregar detalle del producto:
    this.seleccionarProducto(producto);
  }

  this.actualizarTotal();
  this.mostrarSugerencias = false; // Solo ocultar sugerencias, no limpiar búsqueda
}

// Modifica el método seleccionarProducto para que no resetee el campo de búsqueda
seleccionarProducto(producto: Producto): void {
  this.productoForm.patchValue({
    id: producto.id,
    codigo: producto.codigo,
    nombre: producto.nombre,
    unidadMedida: producto.unidadMedida,
    precio: producto.precio,
    stock: producto.stock,
    peso: producto.peso,
    modoUso: producto.modoUso
  });

  this.mostrarSugerencias = false;
}

  actualizarCantidad(producto: ProductoEnVenta, nuevaCantidad: number | string): void {
    // Convertir cantidad a número
    const cantidadNumerica = typeof nuevaCantidad === 'string' 
      ? parseInt(nuevaCantidad, 10) 
      : Number(nuevaCantidad) || 0;
    
    if (cantidadNumerica < 1) {
      return;
    }

    const stockNumerico = Number(producto.stock) || 0;
    if (cantidadNumerica > stockNumerico) {
      this.toastr.warning(`No hay suficiente stock. Disponible: ${stockNumerico}`);
      return;
    }

    producto.cantidad = cantidadNumerica;
    this.actualizarSubtotalProducto(producto);
    this.actualizarTotal();
  }

  actualizarDescuento(producto: ProductoEnVenta, descuento: number | string): void {
    // Convertir descuento a número si viene como string
    let descuentoNumerico = typeof descuento === 'string' 
      ? parseFloat(descuento) 
      : Number(descuento) || 0;
    
    if (descuentoNumerico < 0) {
      descuentoNumerico = 0;
    }
    
    // Asegurar que cantidad y precio sean números
    const cantidad = Number(producto.cantidad) || 0;
    const precio = Number(producto.precio) || 0;
    const subtotalSinDescuento = cantidad * precio;
    
    if (descuentoNumerico > subtotalSinDescuento) {
      this.toastr.warning('El descuento no puede ser mayor al subtotal del producto');
      producto.descuento = Number(subtotalSinDescuento.toFixed(2));
    } else {
      producto.descuento = Number(descuentoNumerico.toFixed(2));
    }

    this.actualizarSubtotalProducto(producto);
    this.actualizarTotal();
  }

  private actualizarSubtotalProducto(producto: ProductoEnVenta): void {
    // Asegurar que todos los valores sean números
    const cantidad = Number(producto.cantidad) || 0;
    const precio = Number(producto.precio) || 0;
    const descuento = Number(producto.descuento) || 0;
    
    const subtotalSinDescuento = cantidad * precio;
    producto.subtotal = Number((subtotalSinDescuento - descuento).toFixed(2));
  }

  private actualizarTotal(): void {
    const subtotal = this.calcularSubtotal();
    const iva = 0; // this.calcularIVA();
    const total = Number((subtotal + iva).toFixed(2));

    this.ventaForm.patchValue({
      total: total
    });
    this.calcularCambio();
  }

  calcularSubtotal(): number {
    let subtotal = 0;
    this.productosEnVenta.forEach(producto => {
      // Asegurar que todos los valores sean números
      const productoSubtotal = Number(producto.subtotal) || 0;
      if (productoSubtotal > 0) {
        subtotal += productoSubtotal;
      } else {
        // Si no hay subtotal calculado, calcularlo
        const cantidad = Number(producto.cantidad) || 0;
        const precio = Number(producto.precio) || 0;
        const descuento = Number(producto.descuento) || 0;
        subtotal += (cantidad * precio) - descuento;
      }
    });
    return Number(subtotal.toFixed(2));
  }

  calcularTotalDescuentos(): number {
    return Number(this.productosEnVenta.reduce((total, producto) => {
      const descuento = Number(producto.descuento) || 0;
      return total + descuento;
    }, 0).toFixed(2));
  }

  calcularIVA(): number {
    return this.calcularSubtotal() * 0.16; // Suponiendo 16% de IVA
  }

  calcularCambio(): void {
    const total = Number(this.ventaForm.get('total')?.value) || 0;
    const dineroRecibido = Number(this.ventaForm.get('dineroRecibido')?.value) || 0;
    const esApartado = this.ventaForm.get('esApartado')?.value || false;
    
    // Si es apartado, el cambio puede ser negativo (deuda pendiente)
    const cambio = esApartado 
      ? Number((dineroRecibido - total).toFixed(2))  // Puede ser negativo para apartados
      : Number(Math.max(0, dineroRecibido - total).toFixed(2)); // Solo positivo para ventas normales

    this.ventaForm.patchValue({
      cambio: cambio
    });
  }

  eliminarProducto(index: number): void {
    this.productosEnVenta.splice(index, 1);
    this.actualizarTotal();
  }

  puedeGenerarTicket(): boolean {
    const total = Number(this.ventaForm.get('total')?.value) || 0;
    const dineroRecibido = Number(this.ventaForm.get('dineroRecibido')?.value) || 0;
    const esApartado = this.ventaForm.get('esApartado')?.value || false;

    // Para apartados, solo necesita tener productos y dinero recibido (puede ser menor al total)
    // Para ventas normales, debe recibir al menos el total
    if (esApartado) {
      return this.productosEnVenta.length > 0 && dineroRecibido > 0;
    }
    
    return this.productosEnVenta.length > 0 && dineroRecibido >= total;
  }

  generarTicket(): void {
    if (this.isLoading) {
      return;
    }

    if (!this.puedeGenerarTicket()) {
      this.toastr.warning('Complete los datos requeridos');
      return;
    }

    // Verificar que el usuarioId esté disponible
    let usuarioId = this.ventaForm.get('usuarioId')?.value;
    if (!usuarioId) {
      const currentUser = this.authService.getCurrentUserSnapshot();
      if (currentUser?.id) {
        usuarioId = currentUser.id;
        this.ventaForm.patchValue({ usuarioId });
      } else {
        this.toastr.error('No se pudo identificar al usuario. Por favor, recarga la página.');
        return;
      }
    }

    const fechaActual = new Date();
    const fechaLocal = new Date(fechaActual.getTime() - fechaActual.getTimezoneOffset() * 60000);
    const esApartado = this.ventaForm.get('esApartado')?.value || false;
    
    const ventaData = {
      usuarioId: Number(usuarioId),
      dineroRecibido: Number(this.ventaForm.get('dineroRecibido')?.value) || 0,
      total: Number(this.ventaForm.get('total')?.value) || 0,
      cambio: Number(this.ventaForm.get('cambio')?.value) || 0,
      esApartado: esApartado,
      metodoPago: this.ventaForm.get('metodoPago')?.value || MetodoPago.EFECTIVO,
      fecha: fechaLocal.toISOString().split('.')[0] + 'Z',
      detalles: this.productosEnVenta.map(producto => ({
        productoId: Number(producto.id),
        cantidad: Number(producto.cantidad) || 1,
        precioUnitario: Number(producto.precio) || 0,
        descuento: Number(producto.descuento) || 0
      }))
    };

    this.isLoading = true;
    const dineroRecibido = Number(this.ventaForm.get('dineroRecibido')?.value) || 0;

    this.ventasService.crearVentaConDetalles(ventaData).subscribe({
      next: (venta) => {
        const processSuccess = async () => {
          this.toastr.success('Venta registrada exitosamente');
          
          // Lógica condicional para generar tickets
          if (this.generarPdf) {
             await this.generarYCompartirPdf(venta);
          }
          
          if (this.generarTicketImpresora) {
             await this.generarYCompartirTicketImpresora(venta);
          }

          this.resetearVenta();
          this.isLoading = false;
        };

        if (esApartado && dineroRecibido > 0 && venta?.id) {
          this.pagosService.registrarAbono(venta.id, dineroRecibido).subscribe({
            next: () => {
              this.toastr.success('Abono registrado exitosamente');
              processSuccess();
            },
            error: (abonoError) => {
              console.error('Error al registrar el abono:', abonoError);
              this.toastr.warning('Venta registrada, pero no se pudo registrar el abono inicial.');
              processSuccess();
            }
          });
        } else {
          processSuccess();
        }
      },
      error: (error) => {
        this.toastr.error('Error al registrar la venta');
        this.isLoading = false;
      }
    });
  }

  async generarYCompartirPdf(venta: any): Promise<void> {
    try {
      const doc = new jsPDF({ orientation: 'portrait', unit: 'pt', format: 'a4' });
      const margin = 36;
      const lineHeight = 18;
      let cursorY = margin;

      doc.setFontSize(16);
      doc.setFont('helvetica', 'bold');
      doc.text('Ticket de Venta', margin, cursorY);

      doc.setFontSize(11);
      doc.setFont('helvetica', 'normal');
      cursorY += lineHeight * 1.5;
      doc.text(`No. Ticket: ${venta.id}`, margin, cursorY);
      doc.text(`Fecha: ${new Date().toLocaleString()}`, margin + 220, cursorY);

      cursorY += lineHeight;
      doc.text(`Total: $${Number(venta.total).toFixed(2)}`, margin, cursorY);

      cursorY += lineHeight * 1.5;
      doc.setFont('helvetica', 'bold');
      doc.text('Productos', margin, cursorY);

      const body = this.productosEnVenta.map((producto, index) => {
        return [
          index + 1,
          producto.nombre,
          producto.cantidad,
          Number(producto.precio).toFixed(2),
          Number(producto.subtotal).toFixed(2)
        ];
      });

      autoTable(doc, {
        startY: cursorY + lineHeight / 2,
        head: [['#', 'Producto', 'Cant.', 'P. Unit.', 'Subtotal']],
        body,
        theme: 'grid',
        styles: { font: 'helvetica', fontSize: 10 }
      });

      const fileName = `ticket-${venta.id}.pdf`;

      if (Capacitor.getPlatform() === 'web') {
        doc.save(fileName);
        this.toastr.success('Ticket PDF descargado');
      } else {
        const pdfBase64 = doc.output('datauristring').split(',')[1];
        
        const savedFile = await Filesystem.writeFile({
          path: fileName,
          data: pdfBase64,
          directory: Directory.Cache
        });

        await Share.share({
          title: 'Ticket de Venta',
          text: `Ticket de venta #${venta.id}`,
          url: savedFile.uri,
          dialogTitle: 'Compartir Ticket PDF'
        });
      }

    } catch (error) {
      console.error('Error generando PDF', error);
      this.toastr.error('No se pudo generar o compartir el PDF');
    }
  }

  async generarYCompartirTicketImpresora(venta: any): Promise<void> {
    try {
      let ticket = "";
      ticket += "      PUNTO DE VENTA      \n";
      ticket += "--------------------------\n";
      ticket += `Folio: ${venta.id}\n`;
      ticket += `Fecha: ${new Date().toLocaleString()}\n`;
      ticket += "--------------------------\n";
      ticket += "Cant  Producto    Importe \n";
      ticket += "--------------------------\n";

      this.productosEnVenta.forEach(p => {
        const nombre = p.nombre.substring(0, 15).padEnd(15);
        const cant = p.cantidad.toString().padStart(4);
        const importe = Number(p.subtotal).toFixed(2).padStart(7);
        ticket += `${cant} ${nombre} ${importe}\n`;
      });

      ticket += "--------------------------\n";
      ticket += `TOTAL: $${Number(venta.total).toFixed(2)}\n`;
      ticket += "--------------------------\n";
      ticket += "    Gracias por su compra \n\n\n";

      const fileName = `ticket-${venta.id}.txt`;
      
      if (Capacitor.getPlatform() === 'web') {
        const blob = new Blob([ticket], { type: 'text/plain' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = fileName;
        a.click();
        window.URL.revokeObjectURL(url);
        this.toastr.success('Ticket de texto descargado');
      } else {
        const savedFile = await Filesystem.writeFile({
          path: fileName,
          data: ticket,
          directory: Directory.Cache,
          encoding: Encoding.UTF8
        });

        await Share.share({
          title: 'Ticket Texto',
          text: ticket,
          url: savedFile.uri,
          dialogTitle: 'Compartir Ticket Impresora'
        });
      }

    } catch (error) {
      console.error('Error generando ticket texto', error);
      this.toastr.error('No se pudo generar el ticket de texto');
    }
  }

  protected resetearVenta(): void {
    this.productosEnVenta = [];
    this.productoForm.reset();
    this.ventaForm.patchValue({
      dineroRecibido: 0,
      total: 0,
      cambio: 0,
      esApartado: false
    });
    this.generarPdf = false;
    this.generarTicketImpresora = false;
    this.setUsuarioId(this.authService.getCurrentUserSnapshot());
    this.busquedaForm.reset();
  }

  // Nueva función para manejar el evento de tecla en la búsqueda
  manejarTeclaBusqueda(event: any): void {
    if (event.key === 'Enter') {
      const termino = this.busquedaForm.get('busqueda')?.value;
      if (termino && termino.length >= 2) {
        this.buscando = true;
        this.productosService.buscarProductos(termino).subscribe({
          next: (productos) => {
            this.productosEncontrados = productos;
            this.buscando = false;
            this.mostrarSugerencias = true;
          },
          error: () => {
            this.toastr.error('Error al buscar productos');
            this.buscando = false;
          }
        });
      }
    }
  }

  private setUsuarioId(user: AuthUser | null): void {
    if (user?.id) {
      this.ventaForm.patchValue({
        usuarioId: user.id
      });
    } else {
      // Intentar obtener desde snapshot como respaldo
      const currentUser = this.authService.getCurrentUserSnapshot();
      if (currentUser?.id) {
        this.ventaForm.patchValue({
          usuarioId: currentUser.id
        });
      } else {
        // Si aún no hay usuario, establecer null pero no causar error
        this.ventaForm.patchValue({
          usuarioId: null
        });
      }
    }
  }
}
interface Producto {
  id: string;
  codigo: string;
  nombre: string;
  tipo: string;
  precio: number;
  stock: number;
  peso: number;
  contenido: string;
  unidadMedida: string;
  cantidad?: number;
  modoUso: string;
}

interface ProductoEnVenta extends Producto {
  cantidad: number;
  descuento: number;
  subtotal: number;
}
